import { useEffect, useState } from "react";
import { Printer, Phone, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import CustomerSearchSelect from "@/components/CustomerSearchSelect";

const SIZES = {
  A4: { w: "297mm", h: "210mm", pad: "20mm", label: 16, toName: 42, toAddr: 27, toPhone: 26, fromName: 21, fromAddr: 16, fromPhone: 16 },
  A5: { w: "210mm", h: "148mm", pad: "13mm", label: 12, toName: 29, toAddr: 18, toPhone: 18, fromName: 16, fromAddr: 12, fromPhone: 12 },
  A6: { w: "148mm", h: "105mm", pad: "9mm", label: 9, toName: 20, toAddr: 13, toPhone: 13, fromName: 12, fromAddr: 10, fromPhone: 10 },
};

export default function DeliveryLabel() {
  const [size, setSize] = useState("A5");
  const [to, setTo] = useState({ name: "", address: "", phone: "", customer_id: "" });
  const [from, setFrom] = useState({ name: "", address: "", phone: "" });
  const s = SIZES[size];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/store");
        setFrom({ name: data.name || "", address: data.address || "", phone: data.phone || "" });
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const printNow = () => { setTimeout(() => window.print(), 120); };

  return (
    <div className="space-y-6" data-testid="delivery-label-page">
      <style>{`@media print {
        @page { size: ${size} landscape; margin: 0; }
        body { visibility: hidden; }
        .label-print-root, .label-print-root * { visibility: visible; }
        .label-print-root { position: fixed; left: 0; top: 0; box-shadow: none !important; border: 0 !important; }
        .no-print { display: none !important; }
      }`}</style>

      <div className="no-print flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Operations</div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Delivery Labels</h1>
          <p className="text-sm text-[#475569] mt-1">Print a shipping label (A4 / A5 / A6, landscape). Fonts auto-adjust to the selected size.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-[#E2E8F0] overflow-hidden" data-testid="label-size-toggle">
            {Object.keys(SIZES).map((sz) => (
              <button key={sz} onClick={() => setSize(sz)} data-testid={`label-size-${sz}`}
                className={`px-3 h-10 text-sm font-medium transition-colors ${size === sz ? "bg-[#0055FF] text-white" : "bg-white text-[#475569] hover:bg-slate-50"}`}>
                {sz}
              </button>
            ))}
          </div>
          <Button onClick={printNow} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2 h-10" data-testid="label-print-btn">
            <Printer className="w-4 h-4" /> Print ({size})
          </Button>
        </div>
      </div>

      <div className="no-print grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* TO */}
        <section className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#0055FF]" />
            <h2 className="text-lg font-display font-medium tracking-tight">Deliver To (recipient)</h2>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Pick from customers (optional)</Label>
            <CustomerSearchSelect
              selectedId={to.customer_id || null}
              onPick={(c) => setTo((t) => c
                ? ({ ...t, customer_id: c.id, name: c.name, phone: c.mobile || "" })
                : ({ ...t, customer_id: "", name: "", phone: "" }))}
              placeholder="Search customers by name or mobile…"
              testId="label-customer-picker"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Recipient Name</Label>
            <Input value={to.name} onChange={(e) => setTo({ ...to, name: e.target.value })} data-testid="label-to-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Address</Label>
            <Textarea rows={4} value={to.address} onChange={(e) => setTo({ ...to, address: e.target.value })} placeholder="Full delivery address" data-testid="label-to-address" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Phone</Label>
            <Input value={to.phone} onChange={(e) => setTo({ ...to, phone: e.target.value })} className="font-mono" data-testid="label-to-phone" />
          </div>
        </section>

        {/* FROM */}
        <section className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <h2 className="text-lg font-display font-medium tracking-tight">From (your company)</h2>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Company Name</Label>
            <Input value={from.name} onChange={(e) => setFrom({ ...from, name: e.target.value })} data-testid="label-from-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Address</Label>
            <Textarea rows={4} value={from.address} onChange={(e) => setFrom({ ...from, address: e.target.value })} data-testid="label-from-address" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Phone</Label>
            <Input value={from.phone} onChange={(e) => setFrom({ ...from, phone: e.target.value })} className="font-mono" data-testid="label-from-phone" />
          </div>
          <p className="text-[11px] text-[#64748B]">From details are pre-filled from Settings → Company; editable here for this print.</p>
        </section>
      </div>

      {/* Preview / print area */}
      <div className="no-print text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Preview ({size} landscape)</div>
      <div className="overflow-auto pb-4">
        <div
          className="label-print-root bg-white border border-[#E2E8F0] shadow-sm text-black"
          data-testid="label-preview"
          style={{ width: s.w, height: s.h, padding: s.pad, display: "flex", flexDirection: "column", boxSizing: "border-box" }}
        >
          {/* TO — 65% */}
          <div style={{ flexBasis: "65%", flexGrow: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ fontSize: s.label }} className="uppercase tracking-[0.15em] text-[#64748B]">To,</div>
            <div style={{ fontSize: s.toName, fontWeight: 700, lineHeight: 1.1 }} className="mt-1">{to.name || "Recipient Name"}</div>
            <div style={{ fontSize: s.toAddr, lineHeight: 1.3 }} className="mt-2 whitespace-pre-line">{to.address || "Delivery address will appear here"}</div>
            {to.phone && (
              <div style={{ fontSize: s.toPhone }} className="mt-2 flex items-center gap-2 font-semibold">
                <Phone size={s.toPhone} /> {to.phone}
              </div>
            )}
          </div>

          {/* FROM — 35%, bottom, right aligned with left indentation */}
          <div style={{ flexBasis: "35%", flexGrow: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "flex-end" }}>
            <div style={{ paddingLeft: "12%", textAlign: "left", borderTop: "1px solid #CBD5E1", paddingTop: "6px", maxWidth: "70%" }}>
              <div style={{ fontSize: s.label }} className="uppercase tracking-[0.15em] text-[#64748B]">From,</div>
              <div style={{ fontSize: s.fromName, fontWeight: 700, lineHeight: 1.1 }}>{from.name || "Your Company"}</div>
              <div style={{ fontSize: s.fromAddr, lineHeight: 1.25 }} className="whitespace-pre-line">{from.address}</div>
              {from.phone && (
                <div style={{ fontSize: s.fromPhone }} className="flex items-center gap-1.5 mt-0.5">
                  <Phone size={s.fromPhone} /> {from.phone}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
